import { useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import DualListPanel from './DualList/DualListPanel';
import DualListOptionRow from './DualList/DualListOptionRow';

interface DualListSelectionDialogEditorProps {
    ruleId: string;
    includeValues: string[];
    excludeValues: string[];
    values: FieldValue[];
    title: string;
    onChange: (data: { value: string[]; notValue: string[] }) => void;
    // The option list is short enough (a handful of identifier/card types) that a search field
    // only adds friction — hidden for those fields, kept for the longer imported-list fields.
    hideSearch?: boolean;
    // Overrides the include/exclude panel titles shown in the page recap only — the dialog itself
    // always uses the generic "Appartient à" / "N'appartient pas à" wording.
    outerLabels?: { include: string; exclude: string };
    // "md" is only worth the extra width for fields with long, unbreakable option names (import
    // batch filenames) that would otherwise get clipped — short option lists (e.g. identifier
    // types) stay at "sm" so the dialog doesn't look oddly stretched with empty space.
    dialogMaxWidth?: 'sm' | 'md';
}

const normalizeForSearch = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

// `ruleId` is accepted for interface parity with the other value editors but isn't needed here:
// option rows are keyed by their own (unique) option name.
function DualListSelectionDialogEditor({ includeValues, excludeValues, values, title, onChange, hideSearch = false, outerLabels, dialogMaxWidth = 'sm' }: DualListSelectionDialogEditorProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [pendingInclude, setPendingInclude] = useState<string[]>(includeValues);
    const [pendingExclude, setPendingExclude] = useState<string[]>(excludeValues);
    const [search, setSearch] = useState('');

    const usableValues = useMemo(() => values.filter(v => v.name && v.label.trim()), [values]);
    const labelByName = useMemo(() => new Map(usableValues.map(v => [v.name, v.label])), [usableValues]);
    const toItems = (names: string[]) => names.map(name => ({ name, label: labelByName.get(name) ?? name }));

    const filteredValues = useMemo(() => {
        if (hideSearch) return usableValues;
        const query = normalizeForSearch(search);
        if (!query) return usableValues;
        return usableValues.filter(option => normalizeForSearch(option.label).includes(query));
    }, [usableValues, search, hideSearch]);

    const handleOpen = () => {
        setPendingInclude(includeValues);
        setPendingExclude(excludeValues);
        setSearch('');
        setOpen(true);
    };

    const handleValidate = () => {
        onChange({ value: pendingInclude, notValue: pendingExclude });
        setOpen(false);
    };

    const addTo = (list: 'include' | 'exclude', optionName: string) => {
        if (list === 'include') {
            setPendingInclude(prev => [...prev, optionName]);
            setPendingExclude(prev => prev.filter(v => v !== optionName));
        } else {
            setPendingExclude(prev => [...prev, optionName]);
            setPendingInclude(prev => prev.filter(v => v !== optionName));
        }
    };

    const removeFrom = (list: 'include' | 'exclude', optionName: string) => {
        if (list === 'include') setPendingInclude(prev => prev.filter(v => v !== optionName));
        else setPendingExclude(prev => prev.filter(v => v !== optionName));
    };

    const includeLabel = t('pages.clients.queryBuilder.simple.dualSelectionDialog.belongsTo');
    const excludeLabel = t('pages.clients.queryBuilder.simple.dualSelectionDialog.doesNotBelongTo');
    const emptyLabel = t('pages.clients.queryBuilder.simple.selectionDialog.noneSelected');

    const summaryLabel = [
        t(`pages.clients.queryBuilder.simple.dualSelectionDialog.summaryBelongs${includeValues.length === 1 ? 'One' : 'Many'}`, { count: includeValues.length }),
        t(`pages.clients.queryBuilder.simple.dualSelectionDialog.summaryDoesNotBelong${excludeValues.length === 1 ? 'One' : 'Many'}`, { count: excludeValues.length }),
    ].join(' · ');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#495057', fontWeight: 600 }}>
                    {summaryLabel}
                </Typography>
                <Button size="small" variant="outlined" onClick={handleOpen} sx={{ textTransform: 'none' }}>
                    {t('pages.clients.queryBuilder.simple.selectionDialog.openButton')}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
                <DualListPanel title={outerLabels?.include ?? includeLabel} color="#2e7d32" items={toItems(includeValues)} emptyLabel={emptyLabel} boxSx={{ height: 100 }} />
                <DualListPanel title={outerLabels?.exclude ?? excludeLabel} color="#c62828" items={toItems(excludeValues)} emptyLabel={emptyLabel} boxSx={{ height: 100 }} />
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth={dialogMaxWidth} fullWidth fullScreen={fullScreen} PaperProps={{ sx: { height: fullScreen ? '100%' : 680 } }}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {!hideSearch && (
                        <TextField
                            size="small"
                            placeholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    )}

                    <Box sx={{ flexShrink: fullScreen ? 1 : 0, flexGrow: fullScreen ? 1 : 0, height: fullScreen ? 'auto' : 220, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                        {filteredValues.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#6c757d', px: 1, py: 1 }}>
                                {t('pages.clients.queryBuilder.simple.noOptionsFound')}
                            </Typography>
                        ) : (
                            filteredValues.map(option => (
                                <DualListOptionRow
                                    key={option.name}
                                    option={option}
                                    membership={pendingInclude.includes(option.name) ? 'include' : pendingExclude.includes(option.name) ? 'exclude' : null}
                                    includeLabel={includeLabel}
                                    excludeLabel={excludeLabel}
                                    onAdd={(list) => addTo(list, option.name)}
                                    onRemove={(list) => removeFrom(list, option.name)}
                                />
                            ))
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, flexGrow: 1, minHeight: 0 }}>
                        <DualListPanel
                            title={includeLabel}
                            color="#2e7d32"
                            items={toItems(pendingInclude)}
                            emptyLabel={emptyLabel}
                            boxSx={{ flexGrow: 1, minHeight: 80 }}
                            onRemove={(name) => removeFrom('include', name)}
                        />
                        <DualListPanel
                            title={excludeLabel}
                            color="#c62828"
                            items={toItems(pendingExclude)}
                            emptyLabel={emptyLabel}
                            boxSx={{ flexGrow: 1, minHeight: 80 }}
                            onRemove={(name) => removeFrom('exclude', name)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
                        {t('pages.clients.queryBuilder.simple.selectionDialog.cancel')}
                    </Button>
                    <Button onClick={handleValidate} variant="contained" sx={{ textTransform: 'none' }}>
                        {t('pages.clients.queryBuilder.simple.selectionDialog.validate')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default DualListSelectionDialogEditor;
